import { gql } from "@apollo/client"
import { convertSubunitIntoUnit } from "@oysterhr/common"
import {
  CountryIsoCode,
  EngagementSeniorityLevelType,
  SalaryBenchmark,
  SalaryCalculation,
} from "@oysterhr/schema"
import { chunk } from "lodash-es"
import { useMemo } from "react"
import { usePublicCostCalculatorQuery } from "@oysterhr/cost-calculator"

type Args = {
  calculationQueries: {
    countryCode: CountryIsoCode
    currencyCode: string
    annualGrossSalary: number
  }[]
}

export const OYSTER_INTELLIGENCE_GET_BULK_SALARY_CALCULATIONS_FOR_SALARY_BENCHMARKS = gql`
  query OysterIntelligenceGetBulkSalaryCalculationsForSalaryBenchmarks(
    $calculationQueries: [SalaryQueryInput!]!
  ) {
    bulkSalaryCalculations(calculationQueries: $calculationQueries) {
      country {
        code
      }
      currency {
        code
      }
      fees {
        oyster {
          feeInEngagementSalaryCurrency {
            rate
            value
            currencyCode
            discount
            discountRate
            withoutDiscount
          }
        }
        vat {
          currencyCode
          rate
          value
        }
      }
      taxes {
        employer {
          contributions {
            amount
            group
            name
          }
        }
      }
      annualGrossSalary
      totals {
        employerCosts
      }
    }
  }
`

type UseBulkSalaryCalculationForSalaryBenchmarksQueryProps = {
  benchmarks: SalaryBenchmark[]
  countryCode: CountryIsoCode
  currencyCode: string
}

export type CostOfHireForBenchmark = {
  seniorityLevel: EngagementSeniorityLevelType
  low: number
  mid: number
  high: number
}

export const useBulkSalaryCalculationForSalaryBenchmarksQuery = ({
  benchmarks,
  countryCode,
  currencyCode,
}: UseBulkSalaryCalculationForSalaryBenchmarksQueryProps) => {
  const calculationQueries = useMemo(() => {
    if (!benchmarks?.length || !countryCode || !currencyCode) {
      return null
    }
    return benchmarks
      ?.map(benchmark => {
        const levels = ["low", "mid", "high"] as const
        return levels.map(level => {
          return {
            annualGrossSalary: Math.round(
              convertSubunitIntoUnit(benchmark[level]?.subunit, currencyCode),
            ),
            countryCode,
            currencyCode,
          }
        })
      })
      .flat()
  }, [benchmarks, countryCode, currencyCode])

  const { data, loading, error } = usePublicCostCalculatorQuery<
    { bulkSalaryCalculations: SalaryCalculation[] },
    Args
  >(OYSTER_INTELLIGENCE_GET_BULK_SALARY_CALCULATIONS_FOR_SALARY_BENCHMARKS, {
    skip: !calculationQueries,
    variables: {
      calculationQueries: calculationQueries as Args["calculationQueries"],
    },
  })

  const calculationsResult = data?.bulkSalaryCalculations

  const costOfHireForBenchmarks = useMemo(() => {
    if (calculationsResult?.length) {
      const allSeniorityLevels = benchmarks?.map(({ level }) => level)
      const calculationForEachLevel = chunk(calculationsResult, 3)
      const calculationsWithLevelEntries = calculationForEachLevel
        .map((calculation, index) => {
          const seniorityLevel = allSeniorityLevels[index]
          const [low, mid, high] = calculation
          return {
            seniorityLevel,
            low: low.totals?.employerCosts,
            mid: mid.totals?.employerCosts,
            high: high?.totals?.employerCosts,
          }
        })
        ?.map(item => [item.seniorityLevel, item] as [typeof item.seniorityLevel, typeof item])
      return Object.fromEntries(calculationsWithLevelEntries) as Record<
        EngagementSeniorityLevelType,
        CostOfHireForBenchmark
      >
    }

    return null
  }, [calculationsResult, benchmarks])

  return {
    calculationsResult,
    costOfHireForBenchmarks,
    loading,
    error,
  }
}
